import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, Users, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const SupervisorCalendar = () => {
    const navigate = useNavigate();
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [loading, setLoading] = useState(false);
    const [schedule, setSchedule] = useState({ siteVisits: [], installations: [] });
    const [selectedDate, setSelectedDate] = useState(today.getDate());

    const fetchSchedule = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.getSupervisorSchedule(year, month);
            setSchedule({
                siteVisits: data.siteVisits || [],
                installations: data.installations || [],
            });
        } catch (err) {
            console.error('Failed to fetch schedule:', err);
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

    // Navigation
    const prevMonth = () => {
        if (month === 1) { setMonth(12); setYear(y => y - 1); }
        else setMonth(m => m - 1);
        setSelectedDate(null);
    };
    const nextMonth = () => {
        if (month === 12) { setMonth(1); setYear(y => y + 1); }
        else setMonth(m => m + 1);
        setSelectedDate(null);
    };
    const goToday = () => {
        setYear(today.getFullYear());
        setMonth(today.getMonth() + 1);
        setSelectedDate(null);
    };

    // Build calendar grid
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const weeks = [];
    let week = new Array(firstDay).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
        week.push(d);
        if (week.length === 7) { weeks.push(week); week = []; }
    }
    if (week.length > 0) {
        while (week.length < 7) week.push(null);
        weeks.push(week);
    }

    // Build events per day
    const getEventsForDay = (day) => {
        if (!day) return { sv: [], inst: [] };
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        const sv = schedule.siteVisits.filter(v => v.date === dateStr);

        // Installation projects span multiple days
        const inst = schedule.installations.filter(p => {
            const start = p.scheduledDate || p.startedDate;
            if (!start) return false;
            const startD = new Date(start);
            const estDays = p.estimatedDays || 7;
            const endD = p.completedDate ? new Date(p.completedDate) : new Date(startD.getTime() + estDays * 86400000);
            const current = new Date(year, month - 1, day);
            return current >= new Date(startD.getFullYear(), startD.getMonth(), startD.getDate())
                && current <= new Date(endD.getFullYear(), endD.getMonth(), endD.getDate());
        });

        return { sv, inst };
    };

    // Selected date detail
    const selectedEvents = selectedDate ? getEventsForDay(selectedDate) : null;
    const isToday = (d) => d && year === today.getFullYear() && month === today.getMonth() + 1 && d === today.getDate();

    return (
        <div className="space-y-4">
            {/* Calendar Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={prevMonth}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <h3 className="text-lg font-semibold min-w-[160px] text-center">
                        {MONTHS[month - 1]} {year}
                    </h3>
                    <Button variant="outline" size="icon" onClick={nextMonth}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={goToday}>Today</Button>
                </div>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </div>

            {/* Legend */}
            <div className="flex gap-4 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" /> SV1</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-500 inline-block" /> SV2</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500 inline-block" /> Installation</span>
            </div>

            <div className="flex flex-col lg:flex-row gap-4">
                {/* Calendar Grid */}
                <div className="flex-1 min-w-0">
                    <div className="border rounded-lg overflow-hidden">
                        {/* Day headers */}
                        <div className="grid grid-cols-7 bg-gray-50 border-b">
                            {DAYS.map(d => (
                                <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>
                            ))}
                        </div>
                        {/* Weeks */}
                        {weeks.map((w, wi) => (
                            <div key={wi} className="grid grid-cols-7 border-b last:border-b-0">
                                {w.map((day, di) => {
                                    const events = getEventsForDay(day);
                                    const hasSv1 = events.sv.some(e => e.visitType === 'SV1');
                                    const hasSv2 = events.sv.some(e => e.visitType === 'SV2');
                                    const hasInst = events.inst.length > 0;
                                    const hasEvents = hasSv1 || hasSv2 || hasInst;
                                    const isSelected = day != null && day === selectedDate;

                                    return (
                                        <div
                                            key={di}
                                            className={`min-h-[70px] p-1 border-r last:border-r-0 transition-colors
                                                ${!day ? 'bg-gray-50' : 'hover:bg-gray-50 cursor-pointer'}
                                                ${isSelected ? 'bg-teal-50 ring-2 ring-teal-500 ring-inset' : ''}
                                                ${isToday(day) ? 'bg-blue-50' : ''}
                                            `}
                                            onClick={() => day && setSelectedDate(day === selectedDate ? null : day)}
                                        >
                                            {day && (
                                                <>
                                                    <div className={`text-xs font-medium mb-1 ${isToday(day) ? 'text-blue-600 font-bold' : 'text-gray-700'}`}>
                                                        {day}
                                                    </div>
                                                    {hasEvents && (
                                                        <div className="flex flex-wrap gap-0.5">
                                                            {hasSv1 && <span className="w-2 h-2 rounded-full bg-blue-500" title="SV1" />}
                                                            {hasSv2 && <span className="w-2 h-2 rounded-full bg-purple-500" title="SV2" />}
                                                            {hasInst && <span className="w-2 h-2 rounded-full bg-orange-500" title="Installation" />}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Side Panel — Day Detail */}
                {selectedDate && selectedEvents && (
                    <div className="w-full lg:w-80 lg:shrink-0">
                        <Card>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm">
                                        {selectedDate} {MONTHS[month - 1]} {year}
                                    </CardTitle>
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedDate(null)}>
                                        <X className="w-3 h-3" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
                                {selectedEvents.sv.length === 0 && selectedEvents.inst.length === 0 && (
                                    <p className="text-sm text-gray-500 text-center py-4">No events this day</p>
                                )}

                                {/* SV Events */}
                                {selectedEvents.sv.map((sv, i) => (
                                    <div
                                        key={`sv-${i}`}
                                        className={`p-3 rounded-lg border-l-4 bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow ${sv.visitType === 'SV1' ? 'border-l-blue-500' : 'border-l-purple-500'
                                            }`}
                                        onClick={() => navigate(`/projects/${sv.projectId}/view`)}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <Badge className={sv.visitType === 'SV1' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                                                {sv.visitType}
                                            </Badge>
                                            <Badge variant="outline" className="text-xs">{sv.status}</Badge>
                                        </div>
                                        <p className="text-sm font-medium">{sv.clientName}</p>
                                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                            <Clock className="w-3 h-3" />
                                            {sv.startTime} - {sv.endTime}
                                        </div>
                                        <div className="text-xs text-gray-500">{sv.slotName}</div>
                                        {sv.siteAddress && (
                                            <div className="flex items-start gap-1 text-xs text-gray-500 mt-1">
                                                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                                                <span className="line-clamp-2">{sv.siteAddress}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Installation Events */}
                                {selectedEvents.inst.map((inst, i) => (
                                    <div
                                        key={`inst-${i}`}
                                        className="p-3 rounded-lg border-l-4 border-l-orange-500 bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={() => navigate(`/projects/${inst.projectId}/view`)}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <Badge className="bg-orange-100 text-orange-700">Installation</Badge>
                                            <Badge variant="outline" className="text-xs">{inst.stageDisplay}</Badge>
                                        </div>
                                        <p className="text-sm font-medium">{inst.clientName}</p>
                                        {inst.siteAddress && (
                                            <div className="flex items-start gap-1 text-xs text-gray-500 mt-1">
                                                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                                                <span className="line-clamp-2">{inst.siteAddress}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                            <Calendar className="w-3 h-3" />
                                            {inst.scheduledDate ? new Date(inst.scheduledDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                                            {inst.estimatedDays ? ` (${inst.estimatedDays} days)` : ''}
                                        </div>
                                        {inst.teams && inst.teams.length > 0 && (
                                            <div className="flex items-start gap-1 text-xs text-gray-500 mt-1">
                                                <Users className="w-3 h-3 mt-0.5 shrink-0" />
                                                <span>{inst.teams.map(t => t.teamName).join(', ')}</span>
                                            </div>
                                        )}
                                        <div className="mt-1">
                                            <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                                                <span>Progress</span>
                                                <span>{inst.progress}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div className="bg-orange-500 h-1.5 rounded-full" style={{ width: `${inst.progress}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupervisorCalendar;
